import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Plus, Pencil, Trash2, X, FileText, Upload, ExternalLink } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import DraggableModal from '../../components/ui/DraggableModal';
import { adminFetch } from '../../utils/adminFetch';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const categories = [
  { value: 'contratos', label: 'Contratos' },
  { value: 'tarifas', label: 'Tarifas' },
  { value: 'guias', label: 'Guías' },
  { value: 'otros', label: 'Otros' }
];

export const AdminSharedDocuments = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({
    title: '',
    description: '',
    category: 'contratos',
    file_url: '',
    file_type: 'pdf'
  });

  useEffect(() => { fetchDocuments(); }, []);

  const fetchDocuments = async () => {
    try {
      const response = await adminFetch(`${API}/admin/shared-documents`);
      if (response.ok) setDocuments(await response.json());
    } catch (error) {
      toast.error('Error al cargar documentos');
    } finally {
      setLoading(false);
    }
  };

  const openModal = (doc = null) => {
    if (doc) {
      setEditing(doc);
      setForm({
        title: doc.title,
        description: doc.description || '',
        category: doc.category,
        file_url: doc.file_url,
        file_type: doc.file_type || 'pdf'
      });
    } else {
      setEditing(null);
      setForm({ title: '', description: '', category: 'contratos', file_url: '', file_type: 'pdf' });
    }
    setShowModal(true);
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file type
    const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Solo se permiten archivos PDF y Word');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await adminFetch(`${API}/admin/upload`, {
        method: 'POST',
        body: formData
      });
      const data = await response.json();
      if (response.ok) {
        setForm(prev => ({ 
          ...prev, 
          file_url: data.url,
          file_type: file.name.endsWith('.pdf') ? 'pdf' : 'doc'
        }));
        toast.success('Archivo subido');
      } else {
        throw new Error(data.detail);
      }
    } catch (error) {
      toast.error('Error al subir archivo');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.file_url) {
      toast.error('Título y archivo son obligatorios');
      return;
    }

    try {
      const url = editing 
        ? `${API}/admin/shared-documents/${editing.document_id}` 
        : `${API}/admin/shared-documents`;
      
      const response = await adminFetch(url, {
        method: editing ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });

      if (!response.ok) throw new Error('Error');
      toast.success(editing ? 'Documento actualizado' : 'Documento creado');
      setShowModal(false);
      fetchDocuments();
    } catch (error) {
      toast.error('Error al guardar');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('¿Eliminar este documento?')) return;
    try {
      await adminFetch(`${API}/admin/shared-documents/${id}`, { 
        method: 'DELETE' 
      });
      toast.success('Documento eliminado');
      fetchDocuments();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const getCategoryLabel = (value) => categories.find(c => c.value === value)?.label || value;

  return (
    <div className="p-6 lg:p-8">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-serif text-3xl mb-2">Documentos Compartidos</h1>
            <p className="text-muted-foreground">Contratos, tarifas y documentos accesibles para todos los clientes</p>
          </div>
          <Button onClick={() => openModal()} className="gap-2">
            <Plus className="w-4 h-4" /> Nuevo Documento
          </Button>
        </div>

        {/* Category filters */}
        <div className="flex gap-2 mb-6 flex-wrap">
          {categories.map(cat => {
            const count = documents.filter(d => d.category === cat.value).length;
            return (
              <span key={cat.value} className="px-3 py-1 text-xs bg-muted border border-border">
                {cat.label} ({count})
              </span>
            );
          })}
        </div>

        {loading ? (
          <div className="space-y-4">{[1,2,3].map(i => <div key={i} className="h-16 bg-muted animate-pulse" />)}</div>
        ) : documents.length === 0 ? (
          <div className="text-center py-12 bg-card border border-border">
            <FileText className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground">No hay documentos compartidos</p>
            <p className="text-sm text-muted-foreground mt-2">Los clientes verán estos documentos en su zona común</p>
          </div>
        ) : (
          <div className="space-y-3">
            {documents.map((doc) => (
              <div key={doc.document_id} className="bg-card border border-border p-4 flex items-center gap-4 group">
                <div className="w-10 h-10 bg-red-500/10 flex items-center justify-center flex-shrink-0">
                  <FileText className="w-5 h-5 text-red-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{doc.title}</span>
                    <span className="text-xs uppercase tracking-widest text-muted-foreground px-2 py-0.5 bg-muted">
                      {getCategoryLabel(doc.category)}
                    </span>
                  </div>
                  {doc.description && (
                    <p className="text-sm text-muted-foreground truncate">{doc.description}</p>
                  )}
                </div>
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <a href={doc.file_url} target="_blank" rel="noopener noreferrer" className="p-2 hover:bg-muted rounded">
                    <ExternalLink className="w-4 h-4" />
                  </a>
                  <button onClick={() => openModal(doc)} className="p-2 hover:bg-muted rounded">
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button onClick={() => handleDelete(doc.document_id)} className="p-2 hover:bg-muted rounded">
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </motion.div>

      {/* Modal */}
      {showModal && (
        <DraggableModal onClose={() => setShowModal(false)}>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-background border border-border w-[28rem] max-w-[calc(100vw-2rem)]">
            <div className="p-4 border-b border-border flex items-center justify-between">
              <h2 className="font-medium">{editing ? 'Editar Documento' : 'Nuevo Documento'}</h2>
              <button onClick={() => setShowModal(false)}><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Título *</label>
                <Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} placeholder="Ej: Contrato de servicios 2024" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Categoría</label>
                <select value={form.category} onChange={e => setForm({...form, category: e.target.value})} className="w-full h-10 px-3 border border-input bg-background rounded-md">
                  {categories.map(cat => <option key={cat.value} value={cat.value}>{cat.label}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Descripción</label>
                <Textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={2} placeholder="Breve descripción del documento" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Archivo *</label>
                {form.file_url ? (
                  <div className="flex items-center gap-2 p-3 bg-muted border border-border">
                    <FileText className="w-5 h-5 text-red-500" />
                    <span className="text-sm flex-1 truncate">{form.file_url.split('/').pop()}</span>
                    <button type="button" onClick={() => setForm({...form, file_url: ''})} className="text-destructive text-sm">Cambiar</button>
                  </div>
                ) : (
                  <div className="border border-dashed border-border p-4 text-center">
                    <input type="file" accept=".pdf,.doc,.docx" onChange={handleUpload} className="hidden" id="doc-upload" />
                    <label htmlFor="doc-upload" className="cursor-pointer">
                      <Upload className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                      <p className="text-sm text-muted-foreground">
                        {uploading ? 'Subiendo...' : 'Haz clic para subir PDF o Word'}
                      </p>
                    </label>
                  </div>
                )}
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowModal(false)}>Cancelar</Button>
                <Button type="submit" disabled={uploading}>Guardar</Button>
              </div>
            </form>
          </motion.div>
        </DraggableModal>
      )}
    </div>
  );
};

export default AdminSharedDocuments;
